/**
 * DeleteAccountModal - Account Deletion Confirmation Flow
 *
 * GDPR/Play Store compliant account deletion feature.
 * Two-step confirmation: Click button -> Type "DELETE" -> Confirm
 *
 * Design: Low-key but obvious, user-friendly, clear consequences
 */

import { useState, useCallback } from 'react';
import { Modal } from './shared/Modal';
import { Button } from './shared';
import { useLanguage } from '@/i18n';
import './DeleteAccountModal.css';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isDeleting: boolean;
}

export function DeleteAccountModal({
  isOpen,
  onClose,
  onConfirm,
  isDeleting,
}: DeleteAccountModalProps) {
  const { t } = useLanguage();
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal closes
  // IMPORTANT: Don't allow closing during deletion to prevent data inconsistency
  const handleClose = useCallback(() => {
    if (!isDeleting) {
      setConfirmText('');
      setError(null);
      onClose();
    } else {
      setError(t('delete_modal.err_in_progress'));
    }
  }, [isDeleting, onClose, t]);

  // Handle confirmation
  const handleConfirm = useCallback(async () => {
    if (confirmText !== 'DELETE') {
      setError(t('delete_modal.err_type_delete'));
      return;
    }

    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('delete_modal.err_failed'));
    }
  }, [confirmText, onConfirm, t]);

  const isConfirmEnabled = confirmText === 'DELETE' && !isDeleting;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('settings.section.delete_account')}
      size="sm"
      preventClose={isDeleting}
    >
      <div className="delete-account-modal">
        {/* Warning Icon */}
        <div className="warning-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>

        {/* Explanation */}
        <div className="warning-text">
          <h3>{t('delete_modal.warn_title')}</h3>
          <p>{t('delete_modal.warn_body')}</p>
          <ul>
            <li>{t('delete_modal.item_transactions')}</li>
            <li>{t('delete_modal.item_accounts')}</li>
            <li>{t('delete_modal.item_categories')}</li>
            <li>{t('delete_modal.item_scheduled')}</li>
            <li>{t('delete_modal.item_devices')}</li>
          </ul>
          <p className="recommendation">
            <strong>{t('signout.rec_label')}</strong> {t('delete_modal.rec_text')}
          </p>
        </div>

        {/* Confirmation Input - Only show if not deleting */}
        {!isDeleting && (
          <div className="confirm-input-group">
            {/* "DELETE" is a literal the user must type (compared against
                input) - it stays English in every language by design. */}
            <label htmlFor="confirm-delete">
              {t('delete_modal.type_confirm_pre')} <strong>DELETE</strong> {t('delete_modal.type_confirm_post')}
            </label>
            <input
              id="confirm-delete"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              disabled={isDeleting}
              autoComplete="off"
              autoFocus
            />
          </div>
        )}

        {/* Deletion Progress - Show while deleting */}
        {isDeleting && (
          <div className="deletion-progress">
            <div className="progress-spinner" />
            <p>{t('delete_modal.deleting_progress')}</p>
            <p className="progress-note">{t('delete_modal.progress_note')}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="modal-actions">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={isDeleting}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={!isConfirmEnabled}
          >
            {isDeleting ? t('accounts.actions.deleting') : t('settings.delete_account.button')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
